import React from 'react';
import { cn } from '@/utils/cn';
import { menuPage, openTimings, type OpenTiming } from '@/data/content';
import Button from '@/components/ui/Button';

interface Category {
    id: string | number;
    category_name: string;
    ficon?: string;
}

interface ResolvedSlot {
    date: string;
    period: string;
    fulfilment: string;
    slotWeight: number;
}

export interface FilterObject {
    categories: Category[];
    activeCategories: (string | number)[];
    onSelectCategories: (ids: (string | number)[]) => void;
    selectedDate: Date | null;
    openTimings: OpenTiming[];
    availableIsoDates: Set<string>;
    onSelectDate: (date: Date | null) => void;
    resolvedSlot?: ResolvedSlot | null;
    includeOutOfStock: boolean;
    onIncludeOutOfStockChange: (v: boolean) => void;
    activeFoodFilter?: { id: number; name: string } | null;
    onRemoveFoodFilter?: () => void;
    onOpenFilters?: (tab?: 'filters' | 'parameters' | 'information') => void;
    onOpenDatePicker?: () => void;
}

interface MenuFiltersProps {
    filterObject: FilterObject;
    isLoading: boolean;
    remainingWeight?: number;
    showFilters?: boolean;
}

const MenuFilters: React.FC<MenuFiltersProps> = ({ filterObject, isLoading, remainingWeight, showFilters = true }) => {
    const {
        categories,
        activeCategories,
        onSelectCategories,
        selectedDate,
        onSelectDate,
        resolvedSlot,
        includeOutOfStock,
        onIncludeOutOfStockChange,
        activeFoodFilter,
        onRemoveFoodFilter,
        onOpenFilters,
        onOpenDatePicker,
    } = filterObject;

    const pillCls = (active: boolean) =>
        `relative flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold transition-all duration-200 border-2 whitespace-nowrap ${
            active
                ? 'border-app-primary text-app-primary bg-app-primary/5'
                : 'bg-app-surface text-app-text border-app-primary/30 hover:border-app-primary'
        }`;

    const activePillCls =
        'flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold bg-app-surface text-app-text border-2 border-app-border whitespace-nowrap';

    const sectionLabelCls =
        'text-[10px] font-black uppercase tracking-[0.2em] text-app-muted mb-2 flex items-center gap-1.5';

    const XIcon = () => (
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
    );

    const FilterIcon = () => (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
        </svg>
    );

    const datePillLabel = selectedDate
        ? `le ${selectedDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`
        : menuPage.filter_today;

    const removeCategory = (id: string | number) => {
        onSelectCategories(activeCategories.filter((c) => String(c) !== String(id)));
    };

    const hasActiveFilters = !!(activeCategories.length > 0 || !includeOutOfStock || activeFoodFilter);

    return (
        <div className={cn('w-full transition-opacity duration-300', isLoading && 'opacity-50 pointer-events-none')}>
            <div className="flex flex-col md:flex-row gap-3">
                {/* ── Order Settings Group ── */}
                <div className="w-full md:w-auto bg-app-surface rounded-2xl border border-app-border p-4">
                    <h3 className={sectionLabelCls}>
                        <span>📋</span> {menuPage.filter_parameters_heading}
                    </h3>

                    <div className="flex flex-wrap gap-2">
                        {/* Date pill */}
                        <Button variant="outline" type="button" onClick={() => onOpenDatePicker?.()} className={pillCls(!!selectedDate)}>
                            📅 {datePillLabel}
                            {selectedDate && (
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        localStorage.removeItem('selectedServiceDate');
                                        onSelectDate(null);
                                    }}
                                    className="w-4 h-4 flex items-center justify-center text-app-muted hover:text-app-text transition-colors"
                                    role="button"
                                    aria-label={menuPage.close_label}
                                >
                                    <XIcon />
                                </span>
                            )}
                        </Button>
                    </div>

                    {/* Progress bar */}
                    {remainingWeight !== undefined && resolvedSlot && (
                        <div className="mt-3 flex items-center gap-3">
                            <span className="text-xs font-bold text-app-muted whitespace-nowrap">
                                🧺 {menuPage.slot_remaining_weight}
                            </span>
                            <div className="flex-1 h-2.5 bg-app-bg border border-app-border rounded-full overflow-hidden">
                                <div
                                    className={cn(
                                        'h-full rounded-full transition-all duration-500',
                                        remainingWeight > 50
                                            ? 'bg-green-500'
                                            : remainingWeight > 20
                                              ? 'bg-amber-400'
                                              : 'bg-red-500'
                                    )}
                                    style={{ width: `${remainingWeight}%` }}
                                />
                            </div>
                            <span className="text-xs font-black text-app-text">{remainingWeight}%</span>
                        </div>
                    )}
                </div>

                {/* ── Filters Group ── */}
                {showFilters && (
                    <div className="w-full md:flex-1 bg-app-surface rounded-2xl border border-app-border p-4">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <h3 className={sectionLabelCls}>
                                    <FilterIcon /> {menuPage.filter_label}
                                </h3>

                                {/* Active removable pills */}
                                {hasActiveFilters && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {/* Category pills */}
                                        {activeCategories.map((catId) => {
                                            const cat = categories.find((c) => String(c.id) === String(catId));
                                            if (!cat) return null;
                                            return (
                                                <Button
                                                    variant="outline"
                                                    key={cat.id}
                                                    type="button"
                                                    onClick={() => onOpenFilters?.('filters')}
                                                    className={cn(activePillCls, 'group cursor-pointer hover:border-app-primary/40 transition-colors')}
                                                >
                                                    {cat.ficon && <span>{cat.ficon}</span>}
                                                    {cat.category_name}
                                                    <span
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            removeCategory(cat.id);
                                                        }}
                                                        className="w-4 h-4 flex items-center justify-center text-app-muted hover:text-app-text transition-colors"
                                                        role="button"
                                                        aria-label={menuPage.close_label}
                                                    >
                                                        <XIcon />
                                                    </span>
                                                </Button>
                                            );
                                        })}

                                        {/* Stock pill */}
                                        {!includeOutOfStock && (
                                            <Button
                                                variant="outline"
                                                type="button"
                                                onClick={() => onOpenFilters?.('filters')}
                                                className={cn(activePillCls, 'group cursor-pointer hover:border-app-primary/40 transition-colors')}
                                            >
                                                ✅ {menuPage.filter_stock_active_label}
                                                <span
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onIncludeOutOfStockChange(true);
                                                    }}
                                                    className="w-4 h-4 flex items-center justify-center text-app-muted hover:text-app-text transition-colors"
                                                    role="button"
                                                    aria-label={menuPage.close_label}
                                                >
                                                    <XIcon />
                                                </span>
                                            </Button>
                                        )}

                                        {/* Food filter pill */}
                                        {activeFoodFilter && (
                                            <Button
                                                variant="outline"
                                                type="button"
                                                onClick={() => onOpenFilters?.('filters')}
                                                className={cn(activePillCls, 'group cursor-pointer hover:border-app-primary/40 transition-colors')}
                                            >
                                                🍽️ {activeFoodFilter.name}
                                                <span
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onRemoveFoodFilter?.();
                                                    }}
                                                    className="w-4 h-4 flex items-center justify-center text-app-muted hover:text-app-text transition-colors"
                                                    role="button"
                                                    aria-label={menuPage.close_label}
                                                >
                                                    <XIcon />
                                                </span>
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Filters button — right aligned, not a pill */}
                            {onOpenFilters && (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => onOpenFilters('filters')}
                                    className="shrink-0 !rounded-xl hover:!bg-app-primary/90"
                                >
                                    <div className="relative">
                                        <FilterIcon />
                                        {hasActiveFilters && (
                                            <span className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center rounded-full bg-white text-app-primary text-[10px] font-black ring-2 ring-app-primary">
                                                {activeCategories.length +
                                                    (!includeOutOfStock ? 1 : 0) +
                                                    (activeFoodFilter ? 1 : 0)}
                                            </span>
                                        )}
                                    </div>
                                    {menuPage.filter_button_label}
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MenuFilters;
